import { ArrowRight, Calendar, CheckCircle2, Clock, Video } from 'lucide-react';

// Duplicated and adapted from ProgramApplicationPage for the
// standalone Catering & Operations Training (€490, 1-day online).
const CateringOperationsApplicationPage = () => {
  const CALENDLY_LINK = 'https://calendly.com/nicolas-gate33/program-application-clone';
  const PRICE_EUR = '€490';

  const HERO_IMAGE =
    'https://images.unsplash.com/photo-1684838200815-36eef38f353c?w=2000&q=85&auto=format&fit=crop';

  const callCovers = [
    { id: 'standing', text: 'Where you stand today — experience, background, current situation' },
    { id: 'goal', text: 'Your operational gaps and what you want to take away from the training' },
    { id: 'fit', text: 'Honest assessment of whether this 1-day training is right for your stage' },
    { id: 'cohort', text: 'The next cohort dates and how the live online format works' },
    { id: 'next', text: 'Enrolment details, payment, and what to prepare before the session' },
  ];

  const processSteps = [
    {
      id: 'book',
      step: '01',
      title: 'Book Your Call',
      description: 'Select a time on Calendly. Limited cohort seats each month.',
    },
    {
      id: 'prep',
      step: '02',
      title: 'Brief Intake',
      description: 'A short questionnaire so we go straight to your operational gaps on the call.',
    },
    {
      id: 'session',
      step: '03',
      title: '15-Minute Fit Call',
      description: 'A direct, operational conversation. No sales pitch.',
    },
    {
      id: 'enrol',
      step: '04',
      title: 'Cohort Enrolment',
      description: `If the training fits, you receive your invitation with the next cohort date and the ${PRICE_EUR} payment link.`,
    },
  ];

  return (
    <div data-testid="catering-operations-application-page" className="min-h-screen">
      {/* Hero */}
      <section
        data-testid="catering-application-hero"
        className="relative min-h-[70vh] flex items-center bg-[#FDFBF7]"
      >
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMAGE}
            alt="Gate 33 private aviation training and consulting — Catering & Operations Application"
            title="Gate 33 private aviation training and consulting — Catering & Operations Application"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/85 to-[#FDFBF7]/30" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 w-full">
          <div className="max-w-2xl">
            <span className="overline inline-block mb-4">Catering &amp; Operations Training</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-6">
              Reserve your seat in the next cohort
            </h1>
            <p className="text-lg text-[#5A5A5A] leading-relaxed mb-8">
              Cohorts are capped to keep the trainer's attention on each candidate's output. Every enrolment starts with a short fit call — 15 minutes, direct, operational — to make sure the training matches your stage before you commit.
            </p>
            <div className="flex flex-wrap items-center gap-6">
              <a
                href={CALENDLY_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="catering-app-hero-calendly"
                className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#1A1A1A] transition-all duration-300"
              >
                Book Application Call
                <ArrowRight size={16} />
              </a>
              <div className="flex items-center gap-3 text-sm text-[#1A1A1A]">
                <span className="text-2xl font-heading text-[#0F0F0F]">{PRICE_EUR}</span>
                <span className="text-[#5A5A5A]">· 1 day · Live online</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Session Details */}
      <section data-testid="catering-application-details" className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <span className="overline inline-block mb-4">What the call covers</span>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6">
                Catering &amp; Operations Training · Fit Call
              </h2>
              <p className="text-[#5A5A5A] leading-relaxed mb-8">
                A direct conversation to understand your operational background and confirm whether the next cohort is the right environment for you. If we don't think it is, we'll tell you.
              </p>

              <ul className="space-y-4 mb-8">
                {callCovers.map((item) => (
                  <li key={item.id} className="flex items-start gap-3">
                    <CheckCircle2 size={20} className="text-[#C5A880] flex-shrink-0 mt-0.5" />
                    <span className="text-[#5A5A5A]">{item.text}</span>
                  </li>
                ))}
              </ul>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-6 bg-[#F5F3EF]">
                  <Clock size={24} className="text-[#C5A880] mb-3" />
                  <h4 className="font-semibold text-[#1A1A1A] mb-1">Duration</h4>
                  <p className="text-sm text-[#5A5A5A]">15 minutes</p>
                </div>
                <div className="p-6 bg-[#F5F3EF]">
                  <Video size={24} className="text-[#C5A880] mb-3" />
                  <h4 className="font-semibold text-[#1A1A1A] mb-1">Format</h4>
                  <p className="text-sm text-[#5A5A5A]">Video call</p>
                </div>
              </div>
            </div>

            <div className="bg-[#1A1A1A] p-8 lg:p-12 flex flex-col justify-center">
              <div className="text-center">
                <Calendar size={48} className="text-[#C5A880] mx-auto mb-6" />
                <h3 className="font-heading text-2xl sm:text-3xl text-[#FDFBF7] mb-4">
                  Reserve your seat
                </h3>
                <p className="text-[#9A9A9A] mb-2">
                  Pick a 15-minute slot on Calendly.
                </p>
                <div className="mb-6">
                  <span className="font-heading text-4xl text-[#FDFBF7]">{PRICE_EUR}</span>
                  <span className="text-[#9A9A9A] text-sm ml-2">/ cohort</span>
                </div>
                <a
                  href={CALENDLY_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="catering-app-calendly"
                  className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300 w-full justify-center"
                >
                  Book on Calendly
                  <ArrowRight size={16} />
                </a>
                <p className="text-[#6A6A6A] text-xs mt-6">Limited seats per cohort</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section data-testid="catering-application-process" className="section-padding bg-[#FDFBF7]">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="overline inline-block mb-4">The Process</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6">
              What to expect
            </h2>
            <p className="text-[#5A5A5A] leading-relaxed">
              Four steps. No sales pitch. Just a fit assessment and a clean enrolment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step) => (
              <div
                key={step.id}
                data-testid={`catering-process-step-${step.id}`}
                className="relative"
              >
                <span className="font-heading text-6xl text-[#E5E0D8] absolute -top-4 left-0">
                  {step.step}
                </span>
                <div className="pt-12">
                  <h3 className="font-heading text-xl mb-3">{step.title}</h3>
                  <p className="text-[#5A5A5A] text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="catering-application-faq" className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="overline inline-block mb-4">Common Questions</span>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight">
                Is this training right for me?
              </h2>
            </div>

            <div className="space-y-8">
              <div className="p-6 border border-[#E5E0D8]">
                <h4 className="font-heading text-lg mb-2">Is this the same as the Gate 33 Program?</h4>
                <p className="text-[#5A5A5A] text-sm">
                  No. The Gate 33 Program is the full cohort transformation — strategy, positioning, recruitment, and the operational reality of private aviation. This standalone training is one focused day on catering &amp; operations only, for crew who already have a clear career direction.
                </p>
              </div>

              <div className="p-6 border border-[#E5E0D8]">
                <h4 className="font-heading text-lg mb-2">Do I need private aviation experience?</h4>
                <p className="text-[#5A5A5A] text-sm">
                  No. The training is built for commercial cabin crew, hospitality professionals, and freelance cabin attendants who need the operational layer no commercial training covers.
                </p>
              </div>

              <div className="p-6 border border-[#E5E0D8]">
                <h4 className="font-heading text-lg mb-2">What does {PRICE_EUR} include?</h4>
                <p className="text-[#5A5A5A] text-sm">
                  One full day of live online training (3 modules · 7 hours), all working materials used during the cohort, and a Gate 33 attestation of completion. VAT included. One-time payment per cohort.
                </p>
              </div>

              <div className="p-6 border border-[#E5E0D8]">
                <h4 className="font-heading text-lg mb-2">When does the next cohort run?</h4>
                <p className="text-[#5A5A5A] text-sm">
                  Cohorts run monthly. Dates and remaining seats are confirmed during your fit call. Once a cohort is full, the next available date is offered.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        data-testid="catering-application-final-cta"
        className="section-dark py-12 md:py-16 border-b border-[#C5A880]/40"
      >
        <div className="container-custom text-center">
          <span className="text-xs uppercase tracking-[0.2em] font-medium text-[#C5A880] inline-block mb-4">
            Cohorts are capped
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-6 text-[#FDFBF7]">
            Reserve your seat before the next cohort closes
          </h2>
          <p className="text-[#9A9A9A] max-w-xl mx-auto mb-10">
            One day. {PRICE_EUR}. Fully online. 15 minutes to confirm the fit and lock your seat.
          </p>
          <a
            href={CALENDLY_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="catering-app-final-cta"
            className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
          >
            Book Application Call
            <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default CateringOperationsApplicationPage;
